import { Chip, Collapse, IconButton, iconSizes } from '@common/ui'
import { IconChevronDown, IconChevronUp } from '@common/ui/icons'
import { useMemo, useState } from 'react'
import { ColumnDef, ColumnSizingState, getCoreRowModel, Row, RowData, useReactTable } from '@tanstack/react-table'
import { AuditEntry } from '../../io/api/audit/data/AuditEntry'
import { renderChangeDetail, renderChangeSummary } from './changeRendering'
import EntityLink from '../../components/data/EntityLink'
import InfiniteScrollTable from '../../components/data/InfiniteScrollTable'
import TablePanel from '../../components/data/TablePanel'
import TargetLink from './TargetLink'
import { actionEmoji } from './actionEmoji'

declare module '@tanstack/react-table' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface TableMeta<TData extends RowData> {
    isOpen: (id: string) => boolean
    toggle: (id: string) => void
  }
}

const formatTimestamp = (iso: string) => new Date(iso).toLocaleString()

type Props = {
  entries: AuditEntry[]
  loading: boolean
  error: Error | null
  hasMore: boolean
  loadMore: () => void
}

const COLUMNS: ColumnDef<AuditEntry>[] = [
  {
    id: 'expand',
    header: '',
    size: 48,
    minSize: 48,
    enableResizing: false,
    cell: ({ row, table }) => {
      const hasDetail = !!row.original.changes && row.original.changes.length > 0
      if (!hasDetail) return null
      const isOpen = table.options.meta!.isOpen(row.original.id)
      return (
        <IconButton
          size="sm"
          aria-label={isOpen ? 'Collapse change details' : 'Expand change details'}
          onClick={() => table.options.meta!.toggle(row.original.id)}
        >
          {isOpen ? (
            <IconChevronUp size={iconSizes.sm} stroke={1.5} />
          ) : (
            <IconChevronDown size={iconSizes.sm} stroke={1.5} />
          )}
        </IconButton>
      )
    },
  },
  {
    id: 'when',
    header: 'When',
    size: 180,
    minSize: 120,
    cell: ({ row }) => formatTimestamp(row.original.timestamp),
  },
  {
    id: 'target',
    header: 'Target',
    size: 320,
    minSize: 140,
    cell: ({ row }) => <TargetLink entry={row.original} />,
  },
  {
    id: 'actor',
    header: 'Actor',
    size: 220,
    minSize: 120,
    cell: ({ row }) => <EntityLink entityId={row.original.actorUserEntityId} />,
  },
  {
    id: 'action',
    header: 'Action',
    size: 200,
    minSize: 140,
    cell: ({ row }) => <Chip size="sm">{`${actionEmoji(row.original.action)} ${row.original.action}`}</Chip>,
  },
  {
    id: 'changes',
    header: 'Changes',
    size: 480,
    minSize: 200,
    meta: { wrap: true },
    cell: ({ row }) => renderChangeSummary(row.original),
  },
]

const AuditLogTable = ({ entries, loading, error, hasMore, loadMore }: Props) => {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({})
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({})

  const tableMeta = useMemo(
    () => ({
      isOpen: (id: string) => !!expanded[id],
      toggle: (id: string) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] })),
    }),
    [expanded]
  )

  const table = useReactTable({
    data: entries,
    columns: COLUMNS,
    state: { columnSizing },
    onColumnSizingChange: setColumnSizing,
    columnResizeMode: 'onChange',
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.id,
    meta: tableMeta,
  })

  const renderDetail = (row: Row<AuditEntry>) => {
    const hasDetail = !!row.original.changes && row.original.changes.length > 0
    if (!hasDetail) return null
    return (
      <Collapse expanded={!!expanded[row.original.id]} keepMounted={false}>
        <div style={{ paddingBlock: 8 }}>{renderChangeDetail(row.original)}</div>
      </Collapse>
    )
  }

  return (
    <TablePanel>
      <InfiniteScrollTable
        table={table}
        loading={loading}
        error={error}
        hasMore={hasMore}
        loadMore={loadMore}
        virtualized={false}
        emptyMessage="No audit entries."
        renderDetail={renderDetail}
      />
    </TablePanel>
  )
}

export default AuditLogTable
